import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Banknote,
  Calculator,
  CheckCircle2,
  Clock3,
  Landmark,
  ReceiptText,
} from 'lucide-react';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useEmployees, usePayroll } from '../../hooks/useHR';
import Button from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';
import EmptyState from '../../components/ui/EmptyState';
import Input from '../../components/ui/Input';
import MetricCard from '../../components/ui/MetricCard';
import Modal from '../../components/ui/Modal';
import Surface from '../../components/ui/Surface';
import type { Payroll } from '../../types/firestore';
import type { DataTableColumn } from '../../types/ui';

type PayrollDraft = Omit<Payroll, 'id' | 'createdAt' | 'updatedAt'>;

const statusLabels: Record<Payroll['status'], string> = {
  draft: 'مسودة',
  calculated: 'محسوب',
  approved: 'معتمد',
  paid: 'مصروف',
};

const statusClasses: Record<Payroll['status'], string> = {
  draft:
    'bg-secondary-100 text-secondary-700 ring-secondary-200 dark:bg-secondary-800 dark:text-secondary-300 dark:ring-secondary-700',
  calculated:
    'bg-warning-50 text-warning-700 ring-warning-200 dark:bg-warning-950/40 dark:text-warning-300 dark:ring-warning-800',
  approved:
    'bg-primary-50 text-primary-700 ring-primary-200 dark:bg-primary-950/40 dark:text-primary-300 dark:ring-primary-800',
  paid:
    'bg-success-50 text-success-700 ring-success-200 dark:bg-success-950/40 dark:text-success-300 dark:ring-success-800',
};

const months = [
  { value: 1, label: 'يناير' },
  { value: 2, label: 'فبراير' },
  { value: 3, label: 'مارس' },
  { value: 4, label: 'أبريل' },
  { value: 5, label: 'مايو' },
  { value: 6, label: 'يونيو' },
  { value: 7, label: 'يوليو' },
  { value: 8, label: 'أغسطس' },
  { value: 9, label: 'سبتمبر' },
  { value: 10, label: 'أكتوبر' },
  { value: 11, label: 'نوفمبر' },
  { value: 12, label: 'ديسمبر' },
];

const currentDate = new Date();

const PayrollManagement: React.FC = () => {
  const { formatAmount } = useCurrency();
  const { employees, fetchEmployees } = useEmployees();
  const {
    organizationPayrolls,
    loading,
    fetchOrganizationPayrolls,
    calculatePayroll,
    createPayroll,
    approvePayroll,
    markPayrollPaid,
  } = usePayroll();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [year, setYear] = useState(currentDate.getFullYear());
  const [draft, setDraft] = useState<PayrollDraft | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);

  useEffect(() => {
    fetchEmployees();
    fetchOrganizationPayrolls();
  }, [fetchEmployees, fetchOrganizationPayrolls]);

  const employeeMap = useMemo(() => {
    return new Map(employees.map(employee => [employee.id, employee]));
  }, [employees]);

  const activeEmployees = useMemo(
    () => employees.filter(employee => employee.isActive),
    [employees]
  );

  const stats = useMemo(() => {
    return {
      total: organizationPayrolls.length,
      calculated: organizationPayrolls.filter(item => item.status === 'calculated')
        .length,
      approved: organizationPayrolls.filter(item => item.status === 'approved').length,
      paid: organizationPayrolls.filter(item => item.status === 'paid').length,
      netTotal: organizationPayrolls.reduce(
        (total, payroll) => total + (payroll.netSalary || 0),
        0
      ),
    };
  }, [organizationPayrolls]);

  const closeModal = () => {
    if (submitting) return;
    setIsModalOpen(false);
    setDraft(null);
  };

  const handleCalculate = async () => {
    if (!employeeId) {
      toast.error('اختر الموظف أولًا');
      return;
    }
    setSubmitting(true);
    try {
      const payrollDraft = await calculatePayroll(employeeId, month, year);
      setDraft(payrollDraft);
      toast.success('تم حساب كشف الراتب');
    } catch (error) {
      toast.error('تعذر حساب كشف الراتب');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreate = async () => {
    if (!draft) return;
    setSubmitting(true);
    try {
      await createPayroll(draft);
      toast.success('تم حفظ كشف الراتب');
      closeModal();
    } catch (error) {
      toast.error('تعذر حفظ كشف الراتب');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (payrollId: string) => {
    setActingId(payrollId);
    try {
      await approvePayroll(payrollId);
      toast.success('تم اعتماد كشف الراتب');
    } catch (error) {
      toast.error('تعذر اعتماد كشف الراتب');
    } finally {
      setActingId(null);
    }
  };

  const handlePaid = async (payrollId: string, paymentSource: 'cash' | 'bank') => {
    setActingId(payrollId);
    try {
      await markPayrollPaid(payrollId, paymentSource);
      toast.success('تم تسجيل صرف الراتب');
    } catch (error) {
      toast.error('تعذر تسجيل صرف الراتب');
    } finally {
      setActingId(null);
    }
  };

  const columns: DataTableColumn<Payroll>[] = [
    {
      key: 'employee',
      header: 'الموظف',
      cell: payroll => {
        const employee = employeeMap.get(payroll.employeeId);
        return (
          <div>
            <p className='font-semibold text-secondary-900 dark:text-white'>
              {employee?.name || payroll.employeeId}
            </p>
            <p className='text-xs text-secondary-500 dark:text-secondary-400'>
              {employee?.employeeId || '-'}
            </p>
          </div>
        );
      },
    },
    {
      key: 'period',
      header: 'الفترة',
      cell: payroll =>
        `${months.find(item => item.value === payroll.payPeriod.month)?.label || payroll.payPeriod.month} ${payroll.payPeriod.year}`,
    },
    {
      key: 'gross',
      header: 'الإجمالي',
      cell: payroll => formatAmount(payroll.earnings.total || 0),
      hideOnMobile: true,
    },
    {
      key: 'deductions',
      header: 'الخصومات',
      cell: payroll => formatAmount(payroll.deductions.total || 0),
      hideOnMobile: true,
    },
    {
      key: 'net',
      header: 'الصافي',
      cell: payroll => (
        <span className='font-semibold text-secondary-950 dark:text-white'>
          {formatAmount(payroll.netSalary || 0)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'الحالة',
      cell: payroll => (
        <div className='space-y-1'>
          <span
            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusClasses[payroll.status]}`}
          >
            {statusLabels[payroll.status]}
          </span>
          <p className='text-xs text-secondary-500 dark:text-secondary-400'>
            {payroll.payrollJournalEntryId ? 'قيد رواتب مرحل' : 'بدون قيد رواتب'}
          </p>
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'الإجراء',
      cell: payroll => (
        <div className='flex flex-wrap gap-2'>
          {(payroll.status === 'draft' || payroll.status === 'calculated') && (
            <Button
              size='sm'
              variant='success'
              loading={actingId === payroll.id}
              leadingIcon={<CheckCircle2 className='h-4 w-4' />}
              onClick={() => handleApprove(payroll.id)}
            >
              اعتماد
            </Button>
          )}
          {payroll.status === 'approved' && (
            <>
              <Button
                size='sm'
                loading={actingId === payroll.id}
                leadingIcon={<Banknote className='h-4 w-4' />}
                onClick={() => handlePaid(payroll.id, 'cash')}
              >
                صرف نقدي
              </Button>
              <Button
                size='sm'
                variant='secondary'
                loading={actingId === payroll.id}
                leadingIcon={<Landmark className='h-4 w-4' />}
                onClick={() => handlePaid(payroll.id, 'bank')}
              >
                صرف بنكي
              </Button>
            </>
          )}
          {payroll.status === 'paid' && (
            <span className='text-xs text-secondary-500 dark:text-secondary-400'>
              مكتمل
            </span>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className='space-y-6'>
      <section className='grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
        <MetricCard
          label='إجمالي الكشوف'
          value={stats.total}
          icon={ReceiptText}
        />
        <MetricCard
          label='بحاجة لاعتماد'
          value={stats.calculated}
          icon={Clock3}
          tone={stats.calculated ? 'amber' : 'green'}
        />
        <MetricCard
          label='معتمدة'
          value={stats.approved}
          icon={CheckCircle2}
          tone='blue'
        />
        <MetricCard
          label='إجمالي الصافي'
          value={formatAmount(stats.netTotal)}
          icon={Banknote}
          tone='green'
        />
      </section>

      <Surface>
        <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <h2 className='text-lg font-bold text-secondary-950 dark:text-white'>
              دورة الرواتب
            </h2>
            <p className='mt-1 text-sm text-secondary-500 dark:text-secondary-400'>
              العملة الرسمية للكشوف هي الجنيه المصري فقط.
            </p>
          </div>
          <Button
            leadingIcon={<Calculator className='h-4 w-4' />}
            onClick={() => {
              setEmployeeId(activeEmployees[0]?.id || '');
              setMonth(currentDate.getMonth() + 1);
              setYear(currentDate.getFullYear());
              setDraft(null);
              setIsModalOpen(true);
            }}
          >
            حساب كشف راتب
          </Button>
        </div>
      </Surface>

      <Surface padding='none'>
        <DataTable
          columns={columns}
          rows={organizationPayrolls}
          rowKey={payroll => payroll.id}
          caption='كشوف الرواتب'
          emptyState={
            <EmptyState
              icon={ReceiptText}
              title={loading ? 'جاري تحميل كشوف الرواتب' : 'لا توجد كشوف رواتب'}
              description='ابدأ بحساب كشف راتب لموظف نشط، ثم راجعه واعتمده قبل الصرف.'
            />
          }
        />
      </Surface>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title='حساب كشف راتب'
        description='سيتم حساب الحضور والإجازات المعتمدة للفترة المختارة.'
        size='lg'
        footer={
          <>
            <Button variant='secondary' onClick={closeModal} disabled={submitting}>
              إلغاء
            </Button>
            <Button
              variant='secondary'
              loading={submitting && !draft}
              leadingIcon={<Calculator className='h-4 w-4' />}
              onClick={handleCalculate}
            >
              حساب
            </Button>
            <Button loading={submitting && Boolean(draft)} disabled={!draft} onClick={handleCreate}>
              حفظ الكشف
            </Button>
          </>
        }
      >
        <div className='grid gap-4 md:grid-cols-3'>
          <div className='space-y-1.5 md:col-span-3'>
            <label className='block text-sm font-semibold text-secondary-700 dark:text-secondary-200'>
              الموظف
            </label>
            <select
              value={employeeId}
              disabled={submitting}
              onChange={event => {
                setEmployeeId(event.target.value);
                setDraft(null);
              }}
              className='min-h-11 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm text-secondary-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:cursor-not-allowed disabled:bg-secondary-100 disabled:text-secondary-500 dark:border-secondary-700 dark:bg-secondary-900 dark:text-white'
            >
              <option value=''>اختر الموظف</option>
              {activeEmployees.map(employee => (
                <option key={employee.id} value={employee.id}>
                  {employee.name} - {employee.employeeId}
                </option>
              ))}
            </select>
          </div>
          <div className='space-y-1.5'>
            <label className='block text-sm font-semibold text-secondary-700 dark:text-secondary-200'>
              الشهر
            </label>
            <select
              value={month}
              disabled={submitting}
              onChange={event => {
                setMonth(Number(event.target.value));
                setDraft(null);
              }}
              className='min-h-11 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm text-secondary-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:cursor-not-allowed disabled:bg-secondary-100 disabled:text-secondary-500 dark:border-secondary-700 dark:bg-secondary-900 dark:text-white'
            >
              {months.map(item => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          <Input
            label='السنة'
            type='number'
            min={2020}
            max={2100}
            value={year}
            disabled={submitting}
            onChange={event => {
              setYear(Number(event.target.value));
              setDraft(null);
            }}
          />
        </div>

        {draft ? (
          <div className='mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
            <Surface padding='sm'>
              <p className='text-xs text-secondary-500 dark:text-secondary-400'>
                إجمالي المستحقات
              </p>
              <p className='mt-1 text-lg font-bold text-secondary-950 dark:text-white'>
                {formatAmount(draft.earnings.total)}
              </p>
            </Surface>
            <Surface padding='sm'>
              <p className='text-xs text-secondary-500 dark:text-secondary-400'>
                الخصومات
              </p>
              <p className='mt-1 text-lg font-bold text-secondary-950 dark:text-white'>
                {formatAmount(draft.deductions.total)}
              </p>
            </Surface>
            <Surface padding='sm'>
              <p className='text-xs text-secondary-500 dark:text-secondary-400'>
                صافي الراتب
              </p>
              <p className='mt-1 text-lg font-bold text-success-700 dark:text-success-300'>
                {formatAmount(draft.netSalary)}
              </p>
            </Surface>
            <Surface padding='sm'>
              <p className='text-xs text-secondary-500 dark:text-secondary-400'>
                الحضور / الإجازات
              </p>
              <p className='mt-1 text-lg font-bold text-secondary-950 dark:text-white'>
                {draft.attendance.actualDays} / {draft.attendance.leaveDays}
              </p>
            </Surface>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default PayrollManagement;
